import { RefetchOptions, RefetchQueryFilters } from '@tanstack/react-query';
import { FeatureFlagMap } from '../FeatureFlags';

export enum AuthPlatform {
  COGNITO = 'COGNITO',
  ZID = 'ZID',
}

export type AuthContextType<TAppPathValue = string, TPagePagsValue = string> = {
  accountId: string;
  accessToken: string;
  featureFlags: FeatureFlagMap;
  isLoading: boolean;
  isAuthenticated: boolean | undefined;
  user: UserWithAdditionalInfo;
  /**
   * @deprecated - Use useAvQuery functions instead
   */
  api: any;
  userPermissions: AvPermissionsType<TAppPathValue, TPagePagsValue>;
  userIsActive: boolean;
  authPlatform: AuthPlatform;
  refreshAccessTokenAndAccountData: () => Promise<void>;
  refetchAdditionalData: (options?: RefetchOptions & RefetchQueryFilters) => void;
  refreshAccessToken: (accountId: string, force?: boolean) => Promise<string>;
};

export type User = {
  accountId?: string;
  userId: string;
  roleId?: string;
  roleName?: string;
  email: any;
  abac?: string;
  userName: string;
  permissions?: string;
  accountApps?: string[];
};
export type UserAccount = { accountId: string; accountName: string; userRoleId: string };
export type UserWithAdditionalInfo = User & { accounts: UserAccount[]; favoriteApps: string[] };

export type AvPermissionsType<TAppPathValue = string, TPagePagsValue = string> = {
  hasAllowedPermission: (options: { app?: TAppPathValue; path: TPagePagsValue; permission?: Permission }) => boolean;

  hasAllowedPermissionToResource: (options: {
    resource: PermissionEntitiesNames;
    category?: PermissionCategoriesNames;
    permission?: Permission;
  }) => boolean;

  hasAllowedEditResourcePermission: (options: { app?: TAppPathValue; path: TPagePagsValue; isNew?: boolean }) => boolean;

  allowedPermissionsToResource: (options: {
    resource: PermissionEntitiesNames;
    category?: PermissionCategoriesNames;
  }) => Partial<Record<Permission, boolean>>;

  allowedPermissionsToPath: (options: { app?: TAppPathValue; path: TPagePagsValue }) => Partial<Record<Permission, boolean>>;

  allowedPermissions: Record<string, Record<Permission, boolean>>;

  isInternalRole: boolean;

  isAvalorInternalRole: boolean;

  isAvalorAdmin: boolean;

  hasOnlyPermission: (options: { app?: TAppPathValue; path: TPagePagsValue; permission?: Permission }) => boolean;

  isBackOfficeAvailable: boolean;

  hasAppInAccount: ({ appPath }: { appPath: string }) => boolean;
};

export enum Permission {
  READ = 'READ',
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  AUDIT = 'AUDIT',
}

export enum PermissionEntitiesNames {
  // Platform
  MODEL = 'MODEL',
  SOURCE = 'SOURCE',
  SOURCE_MAPPING = 'SOURCE_MAPPING',
  WORKFLOW_SCRIPT = 'WORKFLOW_SCRIPT',
  USER = 'USER',
  ACCOUNT = 'ACCOUNT',
  ACCOUNT_SAVED_VIEWS = 'ACCOUNT_SAVED_VIEWS',
  VIEWS = 'VIEWS',
  ROLE = 'ROLE',
  INTEGRATION = 'INTEGRATION',
  ALIAS = 'ALIAS',
  ZCONFIG = 'ZCONFIG',
  AUTHENTICATION = 'AUTHENTICATION',
  MEASUREMENT = 'MEASUREMENT',
  EASM_CONFIGURATION = 'EASM_CONFIGURATION',
  PLAYBOOK = 'PLAYBOOK',
  AUTOMATION_RULE = 'AUTOMATION_RULE',
  FRONTIER_API = 'FRONTIER_API',

  // Explore
  SEARCH = 'SEARCH',
  REPORT = 'REPORT',

  // Multiple Apps
  ANALYTICS = 'ANALYTICS',
  EXCEPTION = 'EXCEPTION',

  // Entity
  OPT = 'OPT',
  SETTINGS = 'SETTINGS',
  INT = 'INT',
  QUERY_RUNNER = 'QUERY_RUNNER',

  // Detection App
  RULE = 'RULE',
  GLOBAL_SCRIPT = 'GLOBAL_SCRIPT',
  LOOKUP_TABLE = 'LOOKUP_TABLE',

  // Risk 360 App
  EXE_RULE = 'EXE_RULE',
  BUILTIN_EXE_RULE = 'BUILTIN_EXE_RULE',

  // Backoffice
  BACKOFFICE = 'BACKOFFICE',

  // IDNP App
  ITDR_CONFIGURATION = 'ITDR_CONFIGURATION',

  // Risk Score
  ASSET_RISK_SCORE = 'ASSET_RISK_SCORE',
  FINDING_RISK_SCORE = 'FINDING_RISK_SCORE',
  USER_RISK_SCORE = 'USER_RISK_SCORE',
  IDENTITY_FINDING_RISK_SCORE = 'IDENTITY_FINDING_RISK_SCORE',
  TENANT_RISK_SCORE = 'TENANT_RISK_SCORE',
}

export enum PermissionAppName {
  PLATFORM = 'PLATFORM',
  EXPLORE = 'EXPLORE',
  ENTITY = 'ENTITY',
  VULNERABILITIES = 'VULNERABILITIES',
  DETECTIONS = 'DETECTIONS',
  INCIDENTS = 'INCIDENTS',
  ASSETS = 'ASSETS',
  RISK360 = 'RISK360',
  THREATS = 'THREATS',
  IDENTITIES = 'IDENTITIES',
}

export enum PermissionModelEntityName {
  TICKET = 'TICKET',
  FINDING = 'FINDING',
  ASSET = 'ASSET',
  INCIDENT = 'INCIDENT',
  ALERT = 'ALERT',
  FACTOR = 'FACTOR',
  POLICYVIOLATION = 'POLICYVIOLATION',
  VIOLATION_TICKET = 'VIOLATIONTICKET',
  EXCEPTION = 'EXCEPTION',
  TENANT = 'TENANT',
  IDENTITY_FINDING = 'IDENTITYFINDING',
  IDENTITY = 'IDENTITY',
  USER = 'USER',
}

export type PermissionCategoriesNames = PermissionAppName | PermissionModelEntityName;
